import { DefaultContext, useQuery } from '@apollo/client'
import React, { useContext, useState } from 'react'

import { AccountType, PayoutMethodAchDocument } from '../../../../../../graphql/search'
import { ApolloClientsContext } from '../../../../../../lib/graphql/apollo'
import { PractitionerSettings } from '../../../../../../lib/practitioner-settings/types'
import { AchAccountForm } from '../AchAccountForm'
import * as Styled from './styled'

export interface AchAccountInformationModalProps {
  show: boolean
  handleHideModal: () => void
}

const AchAccountInformationModal: React.FC<AchAccountInformationModalProps> = ({
  show,
  handleHideModal,
}) => {
  return (
    <Styled.AccountInformationModal
      isOpen={show}
      onBackgroundClick={handleHideModal}
      onEscapeKeydown={handleHideModal}
    >
      <Styled.ModalHeader>
        <Styled.ModalTitle>ACH Account Information</Styled.ModalTitle>
        <Styled.CloseButton onClick={handleHideModal}>
          <span aria-hidden="true">×</span>
        </Styled.CloseButton>
      </Styled.ModalHeader>
      <Styled.ModalContent>
        <Styled.ModalText>
          In order to receive commission and rebate payments, please provide your bank account
          information. Changes may take one payment cycle to go into effect.
        </Styled.ModalText>
        <AchAccountForm handleHideModal={handleHideModal} />
      </Styled.ModalContent>
    </Styled.AccountInformationModal>
  )
}

const accountTypeLabel: Record<AccountType, string> = {
  CHECKING: 'Checking',
  SAVINGS: 'Savings',
}

export interface AchAccountInformationProps {
  className?: string
  settings: PractitionerSettings
}

export const AchAccountInformation: React.FC<AchAccountInformationProps> = ({
  className,
  settings,
}) => {
  const { comma: commaClient } = useContext(ApolloClientsContext)
  const { achFormReceived } = settings.practitioner

  const [showModal, setShowModal] = useState(false)
  const handleShowModal = () => setShowModal(true)
  const handleHideModal = () => setShowModal(false)

  const { data } = useQuery(PayoutMethodAchDocument, {
    client: commaClient,
    context: { useLatestTokenIfLoggedIn: true } as DefaultContext,
  })
  const achDetails = data?.customer?.payoutMethod

  return (
    <section className={className}>
      <Styled.Header>ACH Account Information</Styled.Header>
      <Styled.Description>
        In order to receive commission and rebate payments, please provide your bank account
        information.
      </Styled.Description>
      <Styled.AchInformationBox>
        {!achDetails && achFormReceived ? (
          <Styled.AchColumn>
            <Styled.AchLabel>Bank information on file</Styled.AchLabel>
            <Styled.AchInformationText>ACH Info Submitted</Styled.AchInformationText>
          </Styled.AchColumn>
        ) : !achDetails ? (
          <Styled.AchColumn>
            <Styled.AchInformationText>No account information on file</Styled.AchInformationText>
          </Styled.AchColumn>
        ) : (
          <Styled.AchDetails>
            <Styled.AchRow>
              <Styled.AchColumn>
                <Styled.AchLabel>Name on the bank account</Styled.AchLabel>
                <Styled.AchText>{achDetails.accountHolderName}</Styled.AchText>
              </Styled.AchColumn>
              <Styled.AchColumn>
                <Styled.AchLabel>Bank name</Styled.AchLabel>
                <Styled.AchText>{achDetails.bankName}</Styled.AchText>
              </Styled.AchColumn>
            </Styled.AchRow>
            <Styled.AchRow>
              <Styled.AchColumn>
                <Styled.AchLabel>Account number</Styled.AchLabel>
                <Styled.AchText>{achDetails.accountNumber}</Styled.AchText>
              </Styled.AchColumn>
              <Styled.AchColumn>
                <Styled.AchLabel>Routing number</Styled.AchLabel>
                <Styled.AchText>{achDetails.routingNumber}</Styled.AchText>
              </Styled.AchColumn>
            </Styled.AchRow>
            <Styled.AchRow>
              <Styled.AchColumn>
                <Styled.AchLabel>Account type</Styled.AchLabel>
                <Styled.AchText>{accountTypeLabel[achDetails.accountType]}</Styled.AchText>
              </Styled.AchColumn>
            </Styled.AchRow>
          </Styled.AchDetails>
        )}
        <Styled.AchInformationButton onClick={handleShowModal}>
          {!achDetails && achFormReceived ? 'Update' : !achDetails ? 'Add' : 'Edit'} ACH Information
        </Styled.AchInformationButton>
      </Styled.AchInformationBox>
      <AchAccountInformationModal show={showModal} handleHideModal={handleHideModal} />
    </section>
  )
}
